/**
 * Installation orders activated per week — Order Activation, both views.
 *
 * Ubi, 26 Oct 2026 (relayed by Hugues): *"the number of connections activated per week with
 * the historical evolution"*. Hugues: *"each subco has to be able to see his own stats
 * because these numbers will be used in weekly meetings between Ubi and the partners"*.
 * Drawn first as screen 12 (`mockups/12-weekly-stats-proposal.html`); the rules settled
 * there hold here:
 *
 *   · Time runs ACROSS: one column per week (Monday–Sunday), oldest left, newest right; the
 *     measures are rows. The measure column stays pinned and the table opens at its
 *     newest week (`.tbl-time` in theme.css).
 *   · The table is always there; the charts are IN ADDITION, in a panel under it that folds
 *     away, drawn to the panel's full width.
 *   · A week with no data is EMPTY — "no data" under the week, a dash in the cells, a gap in
 *     the line — never dropped and never drawn as zero.
 *   · One period selector drives the table and the charts.
 *
 * ⚠ Nothing is scoped here. The server answers a contractor with their own contractor's
 * weeks only (`loadWeeklyFlow` → `scopeWeeklyFlow`), so there is nothing in this
 * component a contractor could reveal by changing a filter.
 */

import { formatNumber, type WeekContractorFlow, type WeekFlow } from "@cocon-stats/shared";
import { ChevronDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { CHART_PALETTE, GroupedBars } from "@/components/charts/GroupedBars";
import { Lines } from "@/components/charts/Lines";
import { DEFAULT_PERIOD, PeriodPicker, dm, periodWeeks, weekAxis, weekLabel, type AxisWeek, type Period } from "@/components/period";
import { useElementWidth } from "@/hooks/useElementWidth";
import { useOpensAtNewest } from "@/hooks/useOpensAtNewest";
import { apiFetch } from "@/lib/api-client";

function contractorName(code: string): string {
  return code.charAt(0).toUpperCase() + code.slice(1);
}
function contractorColour(code: string): string {
  return CHART_PALETTE[code] ?? CHART_PALETTE.slate!;
}

type Field = Exclude<keyof WeekContractorFlow, never>;

export function WeeklyActivation({ internal }: { internal: boolean }) {
  const [weeks, setWeeks] = useState<WeekFlow[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [period, setPeriod] = useState<Period>(DEFAULT_PERIOD);
  const [who, setWho] = useState<string>(""); // "" = every contractor this viewer may see
  const [chartsOpen, setChartsOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ weeks: WeekFlow[] }>("/reports/order-activation/weekly")
      .then((data) => {
        if (!cancelled) setWeeks(data.weeks);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Every contractor present in the answer — for a contractor, themselves alone. */
  const codes = useMemo(() => {
    const set = new Set<string>();
    for (const w of weeks ?? []) for (const c of Object.keys(w.byContractor)) set.add(c);
    const order = ["circet", "constructel"];
    return [...set].sort((a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99) || a.localeCompare(b));
  }, [weeks]);
  const chosen = who && codes.includes(who) ? [who] : codes;

  /** The axis: the 52 weeks up to the newest we hold, reaching further back if the data does. */
  const universe = useMemo(() => weekAxis(weeks ?? []), [weeks]);
  const shown = useMemo(() => periodWeeks(universe, period), [universe, period]);

  // The table opens at its newest week.
  const scrollBox = useOpensAtNewest<HTMLDivElement>([shown, who]);

  const [chart1Ref, chart1W] = useElementWidth<HTMLDivElement>();
  const [chart2Ref, chart2W] = useElementWidth<HTMLDivElement>();

  if (failed) {
    return (
      <section className="card p-5 text-sm text-slate-500">The weekly figures could not be loaded.</section>
    );
  }
  if (!weeks) {
    return <section className="card p-5 text-sm text-slate-400">Loading the weekly figures…</section>;
  }

  /** A field summed over the chosen contractors, or null for a week we do not hold. */
  const value = (w: AxisWeek<WeekFlow>, field: Field): number | null => {
    if (!w.data) return null;
    let total = 0;
    for (const c of chosen) {
      const f = w.data.byContractor[c];
      if (!f) continue;
      const v = f[field];
      if (v === null) return null;
      total += v;
    }
    return total;
  };
  const anyMoved = shown.some((w) => (value(w, "moved") ?? 0) !== 0);

  const rows: Array<{ label: string; field: Field; cls: string; total?: boolean }> = [
    { label: "Backlog at start", field: "backlogStart", cls: "text-slate-600" },
    { label: "+ Delivered", field: "delivered", cls: "text-slate-700" },
    ...(anyMoved ? [{ label: "± Moved between contractors", field: "moved" as Field, cls: "text-slate-500" }] : []),
    { label: "− Activated", field: "activated", cls: "font-bold text-emerald-700" },
    { label: "− Left otherwise", field: "leftOtherwise", cls: "text-slate-400" },
    { label: "= Backlog at end", field: "backlogEnd", cls: "font-bold text-slate-900", total: true },
  ];

  const held = shown.filter((w) => w.data).length;
  const scopeName = internal
    ? who
      ? contractorName(who)
      : codes.map(contractorName).join(" + ")
    : codes.map(contractorName).join(" + ") || "—";

  // Charts: per contractor when Unifiber looks at all of them, else delivered vs activated.
  const chartRows = shown.map((w) => {
    const r: Record<string, string | number | undefined> & { label: string; title: string } = {
      label: weekLabel(w.key),
      title: `${weekLabel(w.key)} (${dm(w.start)} – ${dm(w.end)})`,
    };
    for (const c of chosen) {
      const f = w.data?.byContractor[c];
      r[`act_${c}`] = w.data ? (f ? (f.activated ?? undefined) : 0) : undefined;
      r[`end_${c}`] = w.data ? (f ? f.backlogEnd : 0) : undefined;
    }
    r.delivered = value(w, "delivered") ?? undefined;
    r.activated = value(w, "activated") ?? undefined;
    return r;
  });
  const perContractor = chosen.length > 1;
  const chart1Series = perContractor
    ? chosen.map((c) => ({ key: `act_${c}`, label: contractorName(c), color: contractorColour(c) }))
    : [
        { key: "delivered", label: "Delivered", color: CHART_PALETTE.slate! },
        { key: "activated", label: "Activated", color: contractorColour(chosen[0] ?? "circet") },
      ];
  const chart2Series = chosen.map((c) => ({ key: `end_${c}`, label: contractorName(c), color: contractorColour(c) }));

  return (
    <section className="card overflow-hidden p-0">
      <div className="gradient-band flex flex-wrap items-center justify-between gap-3 px-5 py-3">
        <span className="text-[11px] font-bold uppercase tracking-wider text-white">
          {internal ? "Installation Orders Activated Per Week" : "Your Installation Orders Activated Per Week"}
        </span>
        <span className="text-[11px] font-medium text-white/80">
          {scopeName} · {shown.length} weeks, {held} with data
        </span>
      </div>

      {/* Period, and for Unifiber the contractor */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 py-3">
        <PeriodPicker axis={universe} shown={shown} period={period} onChange={setPeriod} />
        {internal && codes.length > 1 && (
          <span className="ml-auto flex flex-wrap items-center gap-2">
            <span className="eyebrow mr-1">Contractor</span>
            {["", ...codes].map((c) => (
              <button key={c || "all"} type="button" className={`chip${who === c ? " chip-active" : ""}`} onClick={() => setWho(c)}>
                {c ? contractorName(c) : "All"}
              </button>
            ))}
          </span>
        )}
      </div>

      {/* The table: weeks across, measures down */}
      <div className="p-5">
        <div ref={scrollBox} className="custom-scrollbar overflow-x-auto">
          <table className="tbl tbl-tight tbl-time">
            <thead>
              <tr>
                <th>Installation orders</th>
                {shown.map((w) => (
                  <th key={w.key} className="text-right" title={`${weekLabel(w.key)}: ${dm(w.start)} – ${dm(w.end)}`}>
                    {weekLabel(w.key)}
                    <br />
                    <span className="text-[10px] font-normal normal-case tracking-normal text-slate-400">
                      {dm(w.start)} – {dm(w.end)}
                    </span>
                    {!w.data ? (
                      <>
                        <br />
                        <span className="pill pill-slate" title="No daily snapshot covers this week">
                          no data
                        </span>
                      </>
                    ) : w.data.partial ? (
                      <>
                        <br />
                        <span className="pill pill-amber" title="The history begins inside this week">
                          partial
                        </span>
                      </>
                    ) : w.data.toDate ? (
                      <>
                        <br />
                        <span className="pill pill-blue" title="This week is still running">
                          to date
                        </span>
                      </>
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.field} className={r.total ? "total" : undefined}>
                  <td className="whitespace-nowrap font-semibold text-slate-700">{r.label}</td>
                  {shown.map((w) => {
                    const v = value(w, r.field);
                    return (
                      <td key={w.key} className={`num text-right ${v === null || v === 0 ? "text-slate-300" : r.cls}`}>
                        {v === null ? "—" : formatNumber(v)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-slate-400">
          Installation orders only, each order once. Activated = left the backlog with its connection MTK in DMP; any
          other exit is &ldquo;left otherwise&rdquo;. A week runs Monday to Sunday of the work day the 06:30 run
          reports. Each column adds up: start + delivered − activated − left otherwise = end. A week with no daily
          snapshot is shown empty.
        </p>
      </div>

      {/* The charts, in addition to the table, in a panel that folds away */}
      <div className="border-t border-slate-100">
        <button
          type="button"
          className="flex w-full items-center justify-between px-5 py-3 text-xs font-semibold text-slate-600"
          onClick={() => setChartsOpen((open) => !open)}
          aria-expanded={chartsOpen}
        >
          <span>Charts</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${chartsOpen ? "" : "-rotate-90"}`} />
        </button>
        <div className={`grid gap-8 px-5 pb-5 xl:grid-cols-2 ${chartsOpen ? "" : "hidden"}`}>
          <div className="min-w-0">
            <p className="eyebrow mb-2">{perContractor ? "Activated per week" : "Delivered and activated per week"}</p>
            <div ref={chart1Ref}>
              {chart1W > 0 &&
                (shown.length <= 12 ? (
                  <GroupedBars rows={chartRows} series={chart1Series} width={chart1W} height={280} />
                ) : (
                  <Lines rows={chartRows} series={chart1Series} width={chart1W} height={280} />
                ))}
            </div>
          </div>
          <div className="min-w-0">
            <p className="eyebrow mb-2">Backlog at week end</p>
            <div ref={chart2Ref}>{chart2W > 0 && <Lines rows={chartRows} series={chart2Series} width={chart2W} height={280} />}</div>
          </div>
        </div>
      </div>
    </section>
  );
}
